/**
 * shared/collision/bvh.ts — deterministic binned-SAH bounding volume hierarchy over primitive AABBs.
 *
 * OWNER: collision agent. Pure; the build is plain float64 arithmetic with a fixed iteration order and an
 * in-place two-pointer partition, so Node, the Worker and every browser build the identical tree (which
 * also fixes the traversal order, hence tie-breaking between equal-fraction hits).
 *
 * Layout (flat arrays, nodes in allocation order, children of an interior node are siblings):
 *   nodeBounds[6*i]   = min xyz, max xyz
 *   nodeData[2*i]     = interior: index of left child (right = left + 1); leaf: first index into primIndex
 *   nodeData[2*i + 1] = leaf: primitive count (> 0); interior: 0
 *   primIndex[]       = primitive ids referenced by leaves
 *
 * Build: SAH with 16 bins on the longest centroid axis (single-axis binning is ~3x cheaper to build than
 * 3-axis; trace cost on Hijacked differs by a few %). Child bounds come from the SAH sweep and child
 * centroid bounds from the partition pass, so each level touches each primitive about twice.
 */

export interface Bvh {
  nodeBounds: Float64Array;
  nodeData: Int32Array;
  primIndex: Int32Array;
  numNodes: number;
  depth: number;
}

const BINS = 16;
const MAX_LEAF = 4;
const TRAVERSE_COST = 1;
const INTERSECT_COST = 1.5;

/** Build a BVH over `n` boxes (Float64Array, 6 per prim: min xyz, max xyz). */
export function buildBvh(boxes: Float64Array, n: number): Bvh {
  const prim = new Int32Array(n);
  const cen = new Float64Array(n * 3);
  const bin = new Uint8Array(n);
  // root bounds + centroid bounds
  const rb = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  const rc = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  for (let i = 0; i < n; i++) {
    prim[i] = i;
    for (let k = 0; k < 3; k++) {
      const lo = boxes[i * 6 + k], hi = boxes[i * 6 + 3 + k];
      const c = (lo + hi) * 0.5;
      cen[i * 3 + k] = c;
      if (lo < rb[k]) rb[k] = lo;
      if (hi > rb[3 + k]) rb[3 + k] = hi;
      if (c < rc[k]) rc[k] = c;
      if (c > rc[3 + k]) rc[3 + k] = c;
    }
  }
  const maxNodes = Math.max(1, 2 * n);
  const nodeBounds = new Float64Array(maxNodes * 6);
  const nodeData = new Int32Array(maxNodes * 2);
  let numNodes = 1;
  let depth = 0;

  const binCount = new Int32Array(BINS);
  const binBox = new Float64Array(BINS * 6);
  const rightBox = new Float64Array(BINS * 6);
  const rightCount = new Int32Array(BINS);

  // work stack: ints (node, first, count, depth) + floats (bounds 6, centroid bounds 6)
  const stackI: number[] = [0, 0, n, 1];
  const stackF: number[] = [...rb, ...rc];
  const B = new Float64Array(6), C = new Float64Array(6);
  const LB = new Float64Array(6), RB = new Float64Array(6);
  while (stackI.length) {
    const d = stackI.pop()!, count = stackI.pop()!, first = stackI.pop()!, node = stackI.pop()!;
    for (let k = 5; k >= 0; k--) C[k] = stackF.pop()!;
    for (let k = 5; k >= 0; k--) B[k] = stackF.pop()!;
    if (d > depth) depth = d;
    const nb = node * 6;
    for (let k = 0; k < 6; k++) nodeBounds[nb + k] = B[k];
    if (count <= MAX_LEAF) { nodeData[node * 2] = first; nodeData[node * 2 + 1] = count; continue; }

    const ex0 = C[3] - C[0], ex1 = C[4] - C[1], ex2 = C[5] - C[2];
    const axis = ex0 >= ex1 && ex0 >= ex2 ? 0 : ex1 >= ex2 ? 1 : 2;
    const lo = C[axis], ext = C[3 + axis] - lo;
    let bestBin = -1, bestCost = Infinity;
    if (ext > 1e-9) {
      const scale = BINS / ext;
      binCount.fill(0);
      for (let b = 0; b < BINS; b++) {
        const q = b * 6;
        binBox[q] = Infinity; binBox[q + 1] = Infinity; binBox[q + 2] = Infinity;
        binBox[q + 3] = -Infinity; binBox[q + 4] = -Infinity; binBox[q + 5] = -Infinity;
      }
      for (let i = first, end = first + count; i < end; i++) {
        const p = prim[i];
        let b = Math.floor((cen[p * 3 + axis] - lo) * scale);
        if (b >= BINS) b = BINS - 1;
        if (b < 0) b = 0;
        bin[p] = b;
        binCount[b]++;
        const o = p * 6, q = b * 6;
        if (boxes[o] < binBox[q]) binBox[q] = boxes[o];
        if (boxes[o + 1] < binBox[q + 1]) binBox[q + 1] = boxes[o + 1];
        if (boxes[o + 2] < binBox[q + 2]) binBox[q + 2] = boxes[o + 2];
        if (boxes[o + 3] > binBox[q + 3]) binBox[q + 3] = boxes[o + 3];
        if (boxes[o + 4] > binBox[q + 4]) binBox[q + 4] = boxes[o + 4];
        if (boxes[o + 5] > binBox[q + 5]) binBox[q + 5] = boxes[o + 5];
      }
      // sweep from the right: rightBox[b] = union of bins b..BINS-1
      let r0 = Infinity, r1 = Infinity, r2 = Infinity, r3 = -Infinity, r4 = -Infinity, r5 = -Infinity, rcnt = 0;
      for (let b = BINS - 1; b > 0; b--) {
        const q = b * 6;
        if (binCount[b]) {
          if (binBox[q] < r0) r0 = binBox[q];
          if (binBox[q + 1] < r1) r1 = binBox[q + 1];
          if (binBox[q + 2] < r2) r2 = binBox[q + 2];
          if (binBox[q + 3] > r3) r3 = binBox[q + 3];
          if (binBox[q + 4] > r4) r4 = binBox[q + 4];
          if (binBox[q + 5] > r5) r5 = binBox[q + 5];
        }
        rcnt += binCount[b];
        rightCount[b] = rcnt;
        rightBox[q] = r0; rightBox[q + 1] = r1; rightBox[q + 2] = r2; rightBox[q + 3] = r3; rightBox[q + 4] = r4; rightBox[q + 5] = r5;
      }
      let l0 = Infinity, l1 = Infinity, l2 = Infinity, l3 = -Infinity, l4 = -Infinity, l5 = -Infinity, lcnt = 0;
      for (let b = 0; b < BINS - 1; b++) {
        const q = b * 6;
        if (binCount[b]) {
          if (binBox[q] < l0) l0 = binBox[q];
          if (binBox[q + 1] < l1) l1 = binBox[q + 1];
          if (binBox[q + 2] < l2) l2 = binBox[q + 2];
          if (binBox[q + 3] > l3) l3 = binBox[q + 3];
          if (binBox[q + 4] > l4) l4 = binBox[q + 4];
          if (binBox[q + 5] > l5) l5 = binBox[q + 5];
        }
        lcnt += binCount[b];
        const rcount = rightCount[b + 1];
        if (lcnt === 0 || rcount === 0) continue;
        const r = (b + 1) * 6;
        const cost = lcnt * area(l0, l1, l2, l3, l4, l5)
          + rcount * area(rightBox[r], rightBox[r + 1], rightBox[r + 2], rightBox[r + 3], rightBox[r + 4], rightBox[r + 5]);
        if (cost < bestCost) {
          bestCost = cost; bestBin = b;
          LB[0] = l0; LB[1] = l1; LB[2] = l2; LB[3] = l3; LB[4] = l4; LB[5] = l5;
          for (let k = 0; k < 6; k++) RB[k] = rightBox[r + k];
        }
      }
    }

    let mid = -1;
    if (bestBin >= 0) {
      const parentArea = area(B[0], B[1], B[2], B[3], B[4], B[5]);
      const leafCost = INTERSECT_COST * count;
      const splitCost = TRAVERSE_COST + INTERSECT_COST * (parentArea > 0 ? bestCost / parentArea : count);
      if (splitCost >= leafCost && count <= 16) { nodeData[node * 2] = first; nodeData[node * 2 + 1] = count; continue; }
      let i = first, j = first + count - 1;
      while (i <= j) {
        if (bin[prim[i]] <= bestBin) { i++; } else { const t = prim[i]; prim[i] = prim[j]; prim[j] = t; j--; }
      }
      mid = i;
    } else {
      // all centroids coincide on the longest axis: split by index (order is deterministic)
      if (count <= 16) { nodeData[node * 2] = first; nodeData[node * 2 + 1] = count; continue; }
      mid = first + (count >> 1);
      boundsOf(boxes, prim, first, mid, LB);
      boundsOf(boxes, prim, mid, first + count, RB);
    }
    const left = numNodes;
    numNodes += 2;
    nodeData[node * 2] = left;
    nodeData[node * 2 + 1] = 0;
    // push right then left (left is built first)
    stackI.push(left + 1, mid, first + count - mid, d + 1);
    for (let k = 0; k < 6; k++) stackF.push(RB[k]);
    centroidBounds(cen, prim, mid, first + count, stackF);
    stackI.push(left, first, mid - first, d + 1);
    for (let k = 0; k < 6; k++) stackF.push(LB[k]);
    centroidBounds(cen, prim, first, mid, stackF);
  }
  return {
    nodeBounds: nodeBounds.slice(0, numNodes * 6),
    nodeData: nodeData.slice(0, numNodes * 2),
    primIndex: prim,
    numNodes,
    depth,
  };
}

function boundsOf(boxes: Float64Array, prim: Int32Array, from: number, to: number, out: Float64Array): void {
  out[0] = out[1] = out[2] = Infinity; out[3] = out[4] = out[5] = -Infinity;
  for (let i = from; i < to; i++) {
    const o = prim[i] * 6;
    for (let k = 0; k < 3; k++) {
      if (boxes[o + k] < out[k]) out[k] = boxes[o + k];
      if (boxes[o + 3 + k] > out[3 + k]) out[3 + k] = boxes[o + 3 + k];
    }
  }
}

function centroidBounds(cen: Float64Array, prim: Int32Array, from: number, to: number, out: number[]): void {
  let a0 = Infinity, a1 = Infinity, a2 = Infinity, b0 = -Infinity, b1 = -Infinity, b2 = -Infinity;
  for (let i = from; i < to; i++) {
    const c = prim[i] * 3;
    const x = cen[c], y = cen[c + 1], z = cen[c + 2];
    if (x < a0) a0 = x; if (x > b0) b0 = x;
    if (y < a1) a1 = y; if (y > b1) b1 = y;
    if (z < a2) a2 = z; if (z > b2) b2 = z;
  }
  out.push(a0, a1, a2, b0, b1, b2);
}

function area(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number {
  const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
  return dx * dy + dy * dz + dz * dx;
}
